import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-border bg-card overflow-hidden transition-colors hover:border-accent/50 rounded-lg">
      <button
        className="w-full flex items-center justify-between p-4 md:p-6 text-left focus:outline-none min-h-[60px]"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <span className="font-heading font-bold text-base md:text-lg pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="p-4 md:p-6 pt-0 text-sm md:text-base text-muted-foreground leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(-1); // No item open by default

  const faqs = [
    {
      question: "What are YouTube tags and why do they matter?",
      answer: "YouTube tags are descriptive keywords you can add to your video to help viewers find your content. While title, thumbnail, and description are the most important pieces of SEO, tags help YouTube's search algorithm understand the context and category of your video, especially if your content contains commonly misspelled words."
    },
    {
      question: "How many tags should I use on my videos?",
      answer: "YouTube allows a maximum of 500 characters for tags. However, quality beats quantity. It's recommended to use 10 to 15 highly relevant tags rather than stuffing 50 broad keywords. SuperTags automatically optimizes the selection to give you the perfect balance."
    },
    {
      question: "How does the AI tag generation work?",
      answer: "Our tool uses advanced Large Language Models (LLMs) to deeply analyze your input title or description. It understands the context, identifies the core entities, and generates highly relevant, high-search-volume keywords that are currently trending in your niche."
    },
    {
      question: "Is SuperTags completely free?",
      answer: "Yes! The core tag generation functionality is completely free for everyone. We believe in providing accessible SEO tools to help creators grow their channels without breaking the bank."
    },
    {
      question: "What is the 'Advanced SEO Mode'?",
      answer: "When you toggle Advanced SEO Mode, the AI focuses on generating 'long-tail keywords'—phrases that are longer and more specific. These keywords have lower search volume but significantly lower competition, making it easier for new channels to rank and get discovered."
    }
  ];

  return (
    <div className="w-full max-w-3xl mx-auto mt-16 md:mt-24 mb-8 md:mb-12 px-4 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-semibold tracking-tight mb-3 md:mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
          Everything you need to know about YouTube tags and SEO.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {faqs.map((faq, index) => (
          <FAQItem 
            key={faq.question}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
          />
        ))}
      </div>
    </div>
  );
};

export default FAQ;
